import React, { forwardRef, type ReactNode } from 'react';
import cn from 'classnames';
import { Button, type ButtonProps, HStack, IconArrowRight, Text, VStack } from '@direct-frontend/components';
import { makeModifierClassname } from '../../lib/makeModifierClassname';
import { TriggerStub } from './TriggerStub';
import styles from './Trigger.module.css';

export type TriggerProps = Omit<ButtonProps, 'color' | 'iconRight' | 'iconLeft'> & {
    placeholder?: string | ReactNode;
    validationState?: 'valid' | 'invalid';
    rightContent?: ReactNode;
    iconLeft?: ReactNode;
    iconRight?: ReactNode;
    caption?: ReactNode;
};

const modifier = makeModifierClassname(styles, 'trigger');
const modifierStack = makeModifierClassname(styles, 'triggerStack');

export const Trigger = forwardRef<HTMLButtonElement, TriggerProps>(function Trigger(props, forwardedRef) {
    const {
        iconLeft,
        iconRight,
        children,
        validationState,
        rightContent,
        placeholder,
        isLoading,
        isDisabled,
        caption,
        ...restProps
    } = props;

    if (isLoading) {
        const linesCount = placeholder && caption ? 3 : 2;

        return <TriggerStub hasRightIcon={Boolean(rightContent)} linesCount={linesCount} />;
    }

    return (
        <Button
            {...restProps}
            color="text"
            isDisabled={isDisabled}
            className={cn(
                restProps.className,
                modifier.className,
                modifier('invalid', validationState === 'invalid'),
                modifier('disabled', isDisabled),
                modifier('big', placeholder && caption)
            )}
            ref={forwardedRef}
            caption=""
        >
            <HStack as="span" alignItems="center" className={modifierStack.className}>
                {iconLeft}

                <HStack
                    as="span"
                    alignItems="center"
                    className={cn(modifierStack.className, styles.triggerContent, modifierStack('mobileWrap', true))}
                >
                    <VStack as="span" gap="2" className={styles.triggerContent} alignItems="flex-start">
                        {placeholder && (
                            <Text size="mc" role="supplementary">
                                {placeholder}
                            </Text>
                        )}

                        {children}

                        {caption && (
                            <Text size="mc" role="supplementary" className={styles.caption}>
                                {caption}
                            </Text>
                        )}
                    </VStack>
                    {rightContent}
                </HStack>

                {iconRight}

                <IconArrowRight size="24" />
            </HStack>
        </Button>
    );
});
